import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	RefreshControl,
	View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-root-toast';
import moment from 'moment';

import {
	ROLE_PERMISSIONS,
} from '../../../common/constants';
import { CREATE_STOCK_DELIVERY_WITH_ITEMS } from '../../../common/graphql/stockDelivery';
import { MODIFY_PURCHASE_ORDER_ITEM } from '../../../common/graphql/purchaseOrderItem';
import { formatPercentage } from '../../../common/utils/utils';
import { useUserContext } from '../../../common/utils/context';

import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useReportDockAction from '../../hooks/useReportDockAction';
import Text from '../../components/text/Text';
import StocktakeQuantitySelector from '../../components/stocktake/StocktakeQuantitySelector';
import {
	Card,
	EmptyState,
	SecondaryButton,
	SectionTitle,
	StatusBadge,
} from '../../design/components';
import { getStatusLabel } from '../../utils/statusLabel';

const getCompletionLabel = (percentageComplete) => {
	if (percentageComplete === -1) {
		return 'No products in purchase order';
	}

	return formatPercentage(percentageComplete);
};

const getSafeDeliveredQuantity = (value) => {
	const parsed = Math.round(Number(value) || 0);
	return Math.max(0, parsed);
};

const getDeliveryVarianceLabel = (deliveredQuantity, orderedQuantity) => {
	const variance = deliveredQuantity - orderedQuantity;
	if (variance > 0) {
		return `Over by ${variance} units`;
	}
	if (variance < 0) {
		return `Under by ${Math.abs(variance)} units`;
	}
	return 'Matches order';
};

const PurchaseOrderDetail = ({ data: purchaseOrder, refetch, onDockChange }) => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const { hasPermission } = useUserContext();
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	const [deliveredQuantityByItemId, setDeliveredQuantityByItemId] = useState({});
	const [createStockDeliveryWithItemsMutation, { loading: isCreatingDelivery }] = useMutation(CREATE_STOCK_DELIVERY_WITH_ITEMS);
	const [modifyPurchaseOrderItemMutation] = useMutation(MODIFY_PURCHASE_ORDER_ITEM);

	const canAddEditStockDeliveries = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.ADD_EDIT_STOCK_DELIVERIES));
	}, [hasPermission]);

	const purchaseOrderItems = useMemo(() => {
		return purchaseOrder?.items || [];
	}, [purchaseOrder?.items]);

	const pendingItems = useMemo(() => {
		return purchaseOrderItems.filter(item => !item?.stockDeliveryItem && !item?.notDeliveredAt);
	}, [purchaseOrderItems]);

	const selectedDeliveredQuantitiesByItemId = useMemo(() => {
		return pendingItems.reduce((acc, item) => {
			const selectedQuantity = getSafeDeliveredQuantity(deliveredQuantityByItemId[item.id]);

			if (selectedQuantity > 0) {
				acc[item.id] = selectedQuantity;
			}
			return acc;
		}, {});
	}, [deliveredQuantityByItemId, pendingItems]);

	const selectedItemsCount = Object.keys(selectedDeliveredQuantitiesByItemId).length;
	const selectedUnitsQuantity = useMemo(() => {
		return Object.values(selectedDeliveredQuantitiesByItemId).reduce((acc, quantity) => acc + quantity, 0);
	}, [selectedDeliveredQuantitiesByItemId]);

	const statusLabel = getStatusLabel(purchaseOrder?.status, 'Pending');
	const notDeliveredItemCount = purchaseOrder?.notDeliveredItemCount || 0;
	const createdLabel = purchaseOrder?.created ? moment(purchaseOrder.created).format('DD MMM YYYY') : 'Unknown';
	const sourceLabel = purchaseOrder?.source?.name || 'Unknown supplier';
	const destinationLabel = purchaseOrder?.destination?.name || 'Unknown warehouse';
	const completionLabel = getCompletionLabel(purchaseOrder?.percentageComplete);

	const setDeliveredQuantityForItem = useCallback((item, nextQuantity) => {
		if (!item?.id) {
			return;
		}

		const safeQuantity = getSafeDeliveredQuantity(nextQuantity);

		setDeliveredQuantityByItemId(currentMap => ({
			...currentMap,
			[item.id]: safeQuantity,
		}));
	}, []);

	const clearDeliveredQuantityForItem = useCallback((purchaseOrderItemId) => {
		setDeliveredQuantityByItemId(currentMap => {
			const nextMap = { ...currentMap };
			delete nextMap[purchaseOrderItemId];
			return nextMap;
		});
	}, []);

	const toggleNotReceived = useCallback(async (item) => {
		if (!item?.id) {
			return;
		}

		try {
			await modifyPurchaseOrderItemMutation({
				variables: {
					input: {
						id: item.id,
						notDeliveredAt: item.notDeliveredAt ? null : new Date().toISOString(),
					},
				},
			});

			clearDeliveredQuantityForItem(item.id);
			await refetch();
			Toast.show(item.notDeliveredAt ? 'Not delivered removed' : 'Marked as not delivered');
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to update delivery status');
		}
	}, [clearDeliveredQuantityForItem, modifyPurchaseOrderItemMutation, refetch]);

	const createDeliveryFromSelectedQuantities = useCallback(async () => {
		if (!purchaseOrder?.id) {
			return;
		}

		const purchaseOrderItemsPayload = {};

		for (const item of pendingItems) {
			const selectedQuantity = getSafeDeliveredQuantity(deliveredQuantityByItemId[item.id]);

			if (!selectedQuantity) {
				continue;
			}

			purchaseOrderItemsPayload[item.id] = selectedQuantity;
		}

		if (!Object.keys(purchaseOrderItemsPayload).length) {
			Toast.show('Select at least one delivered quantity before marking delivered.');
			return;
		}

		try {
			const response = await createStockDeliveryWithItemsMutation({
				variables: {
					purchaseOrder: purchaseOrder.id,
					purchaseOrderItems: purchaseOrderItemsPayload,
				},
			});

			const stockDeliveryId = response?.data?.createStockDeliveryWithItems?.id;
			if (!stockDeliveryId) {
				throw new Error('Stock delivery ID not returned');
			}

			setDeliveredQuantityByItemId({});
			await refetch();
			Toast.show('Delivery created');
			navigation.navigate('StockDeliveryDetailView', { id: stockDeliveryId });
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to mark products as delivered');
		}
	}, [createStockDeliveryWithItemsMutation, deliveredQuantityByItemId, navigation, pendingItems, purchaseOrder?.id, refetch]);

	useReportDockAction(onDockChange, {
		hint: `Selected: ${selectedItemsCount} item${selectedItemsCount === 1 ? '' : 's'} (${selectedUnitsQuantity} units)`,
		label: 'Mark selected delivered',
		onPress: createDeliveryFromSelectedQuantities,
		loading: isCreatingDelivery,
		disabled: !canAddEditStockDeliveries || !selectedItemsCount,
	});

	return (
		<View style={styles.container}>
			<KeyboardAwareScrollView
				contentContainerStyle={styles.contentContainer}
				enableOnAndroid={true}
				extraScrollHeight={32}
				keyboardShouldPersistTaps="handled"
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<Text style={styles.heading} accessibilityRole="header">
					Purchase order {purchaseOrder?.identifier || '—'}
				</Text>
				<Card style={styles.summaryCard}>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Status</Text>
						<Text style={styles.value}>{statusLabel}</Text>
					</View>
					{notDeliveredItemCount > 0 && (
						<View style={styles.summaryRow}>
							<Text style={styles.label}>Not delivered</Text>
							<Text style={[styles.value, styles.valueWarning]}>{`${notDeliveredItemCount} item${notDeliveredItemCount === 1 ? '' : 's'}`}</Text>
						</View>
					)}
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Created</Text>
						<Text style={styles.value}>{createdLabel}</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Supplier</Text>
						<Text style={styles.value}>{sourceLabel}</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Destination</Text>
						<Text style={styles.value}>{destinationLabel}</Text>
					</View>
					<View style={styles.summaryRow}>
						<Text style={styles.label}>Completion</Text>
						<Text style={styles.value}>{completionLabel}</Text>
					</View>
				</Card>

				<SectionTitle title="Purchase order products" />
				<Text style={styles.helperText}>
					Set delivered quantities here. Capture expiry batches later when stocking in the delivery.
				</Text>

				{
					!canAddEditStockDeliveries && (
						<Text style={styles.helperText}>
							You need stock delivery edit permission to mark products as delivered or not delivered.
						</Text>
					)
				}

				{
					!purchaseOrderItems.length && (
						<EmptyState title="No products in this purchase order." />
					)
				}

				{
					purchaseOrderItems.map(item => {
						const isDelivered = item.deliveryOutcome === 'delivered' || Boolean(item.stockDeliveryItem && (
							item.stockDeliveryItem.deliveredAt || item.stockDeliveryItem.receivedAt
						));
						const isNotReceived = item.deliveryOutcome === 'notDelivered' || Boolean(item.notDeliveredAt);
						const isInDelivery = item.deliveryOutcome === 'inDelivery';
						const orderedQuantity = Math.max(0, Math.round(Number(item.quantity) || 0));
						const selectedQuantity = selectedDeliveredQuantitiesByItemId[item.id] || 0;
						const isSelectedForDelivery = selectedQuantity > 0;
						const deliveryVarianceLabel = isSelectedForDelivery
							? getDeliveryVarianceLabel(selectedQuantity, orderedQuantity)
							: null;
						const productName = item.warehouseProduct?.product?.name || 'Unknown product';

						return (
							<View
								key={item.id}
								style={[styles.itemCard, isSelectedForDelivery && styles.itemCardSelected]}
							>
								<View style={styles.itemHeader}>
									<Text style={styles.itemTitle}>{productName}</Text>
									<StatusBadge
										size="small"
										label={isDelivered ? 'Delivered' : (isNotReceived ? 'Not delivered' : (isInDelivery ? 'In delivery' : 'Pending'))}
										tone={isDelivered ? 'success' : (isNotReceived ? 'warning' : 'neutral')}
									/>
								</View>
								<Text style={styles.itemMeta}>
									Ordered quantity: {orderedQuantity} units
								</Text>
								{
									isDelivered ? (
										<>
											<Text style={styles.itemMeta}>
												Marked delivered in: {item.stockDeliveryItem?.stockDelivery?.identifier || 'Unknown delivery'}
											</Text>
											<Text style={styles.itemMeta}>
												Delivery status: {getStatusLabel(item.stockDeliveryItem?.stockDelivery?.status, 'Pending')}
											</Text>
										</>
									) : isNotReceived ? (
										<>
											<Text style={styles.itemMeta}>
												Marked not delivered: {moment(item.notDeliveredAt).format('DD MMM YYYY HH:mm')}
											</Text>
											<View style={styles.itemActionRow}>
												<SecondaryButton
													label="Undo not delivered"
													onPress={() => toggleNotReceived(item)}
													disabled={!canAddEditStockDeliveries}
													style={styles.itemActionButton}
												/>
											</View>
										</>
									) : isInDelivery ? (
										<>
											<Text style={styles.itemMeta}>
												Assigned to delivery: {item.stockDeliveryItem?.stockDelivery?.identifier || 'Unknown delivery'}
											</Text>
											<Text style={styles.itemMeta}>
												Delivery status: {getStatusLabel(item.stockDeliveryItem?.stockDelivery?.status, 'Pending')}
											</Text>
										</>
									) : (
										<>
											<Text style={styles.itemMeta}>
												Delivery quantity to mark: {selectedQuantity} / {orderedQuantity} units
											</Text>
											{deliveryVarianceLabel && (
												<Text style={[
													styles.itemMeta,
													selectedQuantity > orderedQuantity && styles.itemMetaWarning,
												]}
												>
													{deliveryVarianceLabel}
												</Text>
											)}
											<Text style={[
												styles.selectionStatus,
												isSelectedForDelivery && styles.selectionStatusSelected,
											]}
											>
												{isSelectedForDelivery ? 'Selected for delivery' : 'Not selected for delivery'}
											</Text>
											<View style={styles.quantitySelectorContainer}>
												<StocktakeQuantitySelector
													warehouseProduct={item.warehouseProduct}
													value={isSelectedForDelivery ? selectedQuantity : null}
													onChange={value => setDeliveredQuantityForItem(item, value)}
													disabled={!canAddEditStockDeliveries}
												/>
											</View>
											<View style={styles.itemActionRow}>
												<SecondaryButton
													label="Set full quantity"
													style={styles.itemActionButton}
													onPress={() => setDeliveredQuantityForItem(item, orderedQuantity)}
													disabled={!canAddEditStockDeliveries}
												/>
												<SecondaryButton
													label="Mark not delivered"
													style={styles.itemActionButton}
													onPress={() => toggleNotReceived(item)}
													disabled={!canAddEditStockDeliveries}
												/>
												{
													isSelectedForDelivery && (
														<SecondaryButton
															label="Clear selection"
															variant="tonal"
															style={styles.itemActionButton}
															onPress={() => clearDeliveredQuantityForItem(item.id)}
															disabled={!canAddEditStockDeliveries}
														/>
													)
												}
											</View>
										</>
									)
								}
							</View>
						);
					})
				}
			</KeyboardAwareScrollView>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		flex: 1,
	},
	contentContainer: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.xl,
		gap: space.md,
	},
	heading: {
		...type.title,
		color: colors.text,
	},
	summaryCard: {
		gap: space.sm,
	},
	summaryRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: space.md,
	},
	label: {
		...type.secondary,
		color: colors.textSecondary,
	},
	value: {
		...type.secondaryStrong,
		color: colors.text,
		flexShrink: 1,
		textAlign: 'right',
	},
	valueWarning: {
		color: colors.warning,
	},
	helperText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemCard: {
		backgroundColor: colors.raised,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		borderRadius: radius.card,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xs,
	},
	itemCardSelected: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
	},
	itemHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
	},
	itemTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	itemMeta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemMetaWarning: {
		color: colors.warning,
	},
	selectionStatus: {
		...type.secondarySemibold,
		color: colors.textSecondary,
	},
	selectionStatusSelected: {
		color: colors.accent,
	},
	quantitySelectorContainer: {
		marginTop: space.xs,
		maxWidth: 240,
	},
	itemActionRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginTop: space.sm,
	},
	itemActionButton: {
		flexGrow: 1,
	},
});

export default DetailContainer(PurchaseOrderDetail);
